// Welle 4 — Pending Mapper queue with review actions (V1: review/dismiss).
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Inbox, Check, X } from "lucide-react";
import { toast } from "sonner";
import { fetchPendingQueue, reviewPendingItem } from "../../../api/adminDashboardApi";
import { SectionCard, EmptyHint, Pill } from "./AdminV2Kit";

export default function PendingQueueCard({ summary, developer = false }) {
  const { t } = useTranslation("admin");
  const [items, setItems] = useState([]);
  const [busyId, setBusyId] = useState(null);
  const total = summary?.total ?? 0;

  useEffect(() => {
    let alive = true;
    if (total > 0) {
      fetchPendingQueue({ limit: developer ? 30 : 8 })
        .then((d) => alive && setItems(d.items || []))
        .catch(() => {});
    } else {
      setItems([]);
    }
    return () => { alive = false; };
  }, [total, developer]);

  const act = async (item, action) => {
    setBusyId(item.id);
    try {
      await reviewPendingItem(item.kind, item.id, action);
      setItems((prev) => prev.filter((i) => i.id !== item.id));
      toast.success(t("modules.pending.updated"));
    } catch {
      toast.error(t("modules.pending.updateError"));
    } finally {
      setBusyId(null);
    }
  };

  return (
    <SectionCard
      title={t("modules.pending.title")}
      icon={Inbox}
      status={total > 0 ? "attention" : "healthy"}
      testid="adminv2-pending-card"
      actions={
        <div className="text-right">
          <div className="text-2xl font-semibold text-zinc-100 tabular-nums">{total}</div>
          <div className="text-[10px] uppercase tracking-widest text-zinc-500">
            {t("modules.pending.open")}
          </div>
        </div>
      }
    >
      {total === 0 ? (
        <EmptyHint testid="adminv2-pending-empty">{t("modules.pending.empty")}</EmptyHint>
      ) : (
        <ul className="space-y-2">
          {items.map((item) => (
            <li
              key={item.id}
              data-testid={`adminv2-pending-item-${item.id}`}
              className="rounded-xl border border-zinc-800 bg-zinc-950/50 px-3.5 py-2.5"
            >
              {/* Welle 4.1 — the raw term is the hero: full text, never
                  truncated ("Potrebno renoviranje", not "Po…"). Buttons
                  shrink to icon-only with tooltips. */}
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <span
                      data-testid={`adminv2-pending-term-${item.id}`}
                      className="whitespace-normal break-words text-sm font-medium leading-snug text-zinc-100"
                    >
                      „{item.raw_value}“
                    </span>
                    <Pill tone="amber">
                      {t(`modules.pending.kinds.${item.kind}`, { defaultValue: item.kind })}
                      {item.domain ? ` · ${item.domain}` : ""}
                    </Pill>
                  </div>
                  <div className="mt-0.5 text-[11px] text-zinc-500">
                    {t("modules.pending.seen", { count: item.seen_count })}
                    {item.source ? ` · ${item.source}` : ""}
                    {developer && item.context?.make_canonical
                      ? ` · ${item.context.make_canonical}`
                      : ""}
                  </div>
                </div>
                <div className="flex shrink-0 items-center gap-1">
                  <button
                    type="button"
                    data-testid={`adminv2-pending-review-${item.id}`}
                    disabled={busyId === item.id}
                    onClick={() => act(item, "reviewed")}
                    title={t("modules.pending.markReviewed")}
                    aria-label={t("modules.pending.markReviewed")}
                    className="inline-flex h-7 w-7 items-center justify-center rounded-full border border-emerald-500/40 bg-emerald-500/10 text-emerald-300 hover:bg-emerald-500/20 disabled:opacity-40 transition-colors"
                  >
                    <Check size={13} />
                  </button>
                  <button
                    type="button"
                    data-testid={`adminv2-pending-dismiss-${item.id}`}
                    disabled={busyId === item.id}
                    onClick={() => act(item, "dismissed")}
                    title={t("modules.pending.dismiss")}
                    aria-label={t("modules.pending.dismiss")}
                    className="inline-flex h-7 w-7 items-center justify-center rounded-full border border-zinc-700 bg-zinc-800/60 text-zinc-400 hover:text-zinc-200 disabled:opacity-40 transition-colors"
                  >
                    <X size={13} />
                  </button>
                </div>
              </div>
            </li>
          ))}
        </ul>
      )}
    </SectionCard>
  );
}
